import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete } from '@shopify/polaris';
import { api } from '../utils/api';

// Suggestions are the tags already used in the shop, fetched once per page session.
let suggestionsPromise = null;

function loadSuggestions() {
  if (!suggestionsPromise) {
    suggestionsPromise = api
      .get('/products/tags/suggestions')
      .then((response) => response.data || {})
      .catch(() => {
        suggestionsPromise = null;
        return {};
      });
  }
  return suggestionsPromise;
}

// The shop's existing customer or product tags, for suggestions and display.
export function useTagSuggestions(kind) {
  const [tags, setTags] = useState([]);

  useEffect(() => {
    let active = true;
    loadSuggestions().then((suggestions) => {
      if (!active) return;
      setTags(kind === 'customer' ? suggestions.customerTags || [] : suggestions.productTags || []);
    });
    return () => {
      active = false;
    };
  }, [kind]);

  return tags;
}

// Show a saved tag with the capitalisation it has in Shopify (tags match
// case-insensitively, so "vip" saved here is Shopify's "VIP").
export function displayTag(tag, shopTags) {
  const match = (shopTags || []).find((shopTag) => shopTag.toLowerCase() === String(tag).toLowerCase());
  return match || tag;
}

// Pick a customer or product tag. Suggests tags already in use (kind "customer" or
// "product") but accepts any typed tag, since a merchant may set up a limit before
// tagging anyone. A typed tag that matches an existing one takes that tag's
// capitalisation.
function TagPicker({ kind, label, value, onChange }) {
  const [input, setInput] = useState(value || '');
  const allTags = useTagSuggestions(kind);

  const options = useMemo(() => {
    const typed = input.trim();
    const matches = allTags
      .filter((tag) => !typed || tag.toLowerCase().includes(typed.toLowerCase()))
      .slice(0, 20)
      .map((tag) => ({ value: tag, label: tag }));
    if (typed && !matches.some((option) => option.value.toLowerCase() === typed.toLowerCase())) {
      matches.unshift({ value: typed, label: `Use "${typed}"` });
    }
    return matches;
  }, [allTags, input]);

  const handleInput = (next) => {
    setInput(next);
    onChange(displayTag(next.trim(), allTags));
  };

  const handleSelect = (selected) => {
    const tag = selected[0] || '';
    setInput(tag);
    onChange(tag);
  };

  return (
    <Autocomplete
      options={options}
      selected={value ? [value] : []}
      onSelect={handleSelect}
      textField={
        <Autocomplete.TextField
          label={label}
          value={input}
          onChange={handleInput}
          placeholder="Start typing a tag..."
          autoComplete="off"
        />
      }
    />
  );
}

export default TagPicker;
